import type { ComponentProps, JSX, ReactElement } from 'react';

import { Button } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';
import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { TabLink, TabLinks } from '@change-corgi/design-system-legacy/components/navigation';
import { DesignSystemLegacy } from '@change-corgi/design-system-legacy/core';
import { sunriseTheme } from '@change-corgi/design-system-legacy/theme';

import { ShareLinks } from './components/ShareLinks';
import { usePageHeading } from './hooks/usePageHeading';

export function PageHeading(): JSX.Element {
	const {
		data: { showGuidesLink, urlBase, isGuide, showSapCta },
	} = usePageHeading();

	return (
		<>
			<div className="relative container mt-2 px-0">
				<DesignSystemLegacy theme={sunriseTheme}>
					<TabLinks
						selected={isGuide ? 'guides' : 'news'}
						sx={{
							li: {
								fontWeight: 400,
								color: 'sunrise.semantic.typography.weak',
								'&:has([data-active-link="true"])': {
									fontWeight: 700,
									color: 'sunrise.semantic.typography.interactive.default',
									borderColor: 'sunrise.semantic.stroke.selected',
								},
								'&:has(a:focus), &:has(a:active)': {
									fontWeight: 700,
									color: 'sunrise.semantic.typography.interactive.default',
									borderColor: 'sunrise.semantic.stroke.selected',
								},

								':hover': {
									color: 'sunrise.semantic.typography.interactive.default',
									borderColor: 'sunrise.semantic.stroke.selected',
									cursor: 'pointer',
									backgroundColor: 'sunrise.semantic.fill.hover',
								},
							},
						}}
					>
						<TabLink name="news" to={urlBase} data-active-link={!isGuide}>
							<Translate value="fe.components.news_header.news" />
						</TabLink>
						{/* FIXME update types of Tabs to allow for conditional tab links */}
						{
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(showGuidesLink ? (
								<TabLink name="guides" to={`${urlBase}?category=guides`} data-active-link={!!isGuide}>
									<Translate value="fe.components.footer.guides" />
								</TabLink>
							) : null) as unknown as ReactElement<ComponentProps<typeof TabLink>>
						}
					</TabLinks>
					<div className="absolute top-0 right-8 z-10">
						{/* share links are using user agent, which is incompatible with SSR server-caching, so we need to render them on the client only */}
						<ClientRender>
							<ShareLinks />
						</ClientRender>
					</div>
				</DesignSystemLegacy>
			</div>
			{showSapCta && (
				<div className="sticky top-0 left-0 z-50 bg-white px-4 py-8 text-center">
					<Button
						as="a"
						variant="primary"
						className="w-full max-w-sm"
						href="/start-a-petition?source_location=news_page"
						size="large"
						data-qa="start-a-petition-cta"
					>
						<Translate value="fe.components.header.start_a_petition" />
					</Button>
				</div>
			)}
		</>
	);
}
